// 치과 환자 상세 「보낸 소식」 카드 (2026-10-06) — 계약 docs/계약_치과_보낸소식_웹_2026-10-06.md §1 · 기획 §6-1
//  - 조회 전용. 다른 카드와 독립 로딩(실패가 상세 전체를 가리지 않음) + [다시 시도]. 앵커 #notices(소식 현황 환자명 링크 착지).
//  - 문구는 전부 응답 texts·행 label·eventLabel·resultLabel·stageLabel 그대로(웹 사전 없음). labels 범례는 화면에 나열하지 않는다.
//  - 금지(계약 머리): 점수·등급·경보색·"위험/응급/이상" 라벨·답 내용·진료 유형·trigger·phraseId·편집/재발송/수동발송·내보내기·인쇄·복사.
//  - 표 열: 날짜·시각 | 종류 | 환자가 본 문구(texts.textColumn) | 결과. 사건 행은 문구 칸 비우고 결과 칸에 eventLabel(+expired 면 resultLabel).
//  - 더 보기: 첫 호출 limit 50 → 20행씩 클라이언트 펼침, 다 펼쳤고 nextCursor 있으면 cursor 로 재호출.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Send } from 'lucide-react';
import { unifiedStyles as U } from '../../styles/Styles';
import {
    listPatientNotices, describeNoticesError, TIMELINE_FIRST_LIMIT, TIMELINE_PAGE, UPCOMING_FOLD, fmtDayKey, fmtAt, isSendEvent,
} from '../../services/clinicNoticesApi';

// 2026-10-08 알림 파트: 치과 안내(kind 'clinic_notice') 행 — 라벨은 서버 행 label("치과 안내 · 휴진" 등 고정 사전) 그대로, 비어 있을 때만 이 폴백
const NOTICE_LABEL_FALLBACK = '치과 안내';

function ClinicPatientNotices({ pid }) {
    const [data, setData] = useState(null);      // 첫 응답(texts·upcoming·todayKey 등)
    const [items, setItems] = useState([]);      // 누적 타임라인 행
    const [nextCursor, setNextCursor] = useState(null);
    const [shown, setShown] = useState(TIMELINE_PAGE);
    const [loading, setLoading] = useState(true);
    const [moreBusy, setMoreBusy] = useState(false);
    const [error, setError] = useState('');
    const [moreError, setMoreError] = useState('');
    const [upcomingOpen, setUpcomingOpen] = useState(false);
    const anchorRef = useRef(null);

    const load = useCallback(async () => {
        setLoading(true); setError(''); setMoreError('');
        try {
            const r = await listPatientNotices(pid, { limit: TIMELINE_FIRST_LIMIT });
            setData(r);
            setItems(r.items);
            setNextCursor(r.nextCursor || null);
            setShown(TIMELINE_PAGE);
        } catch (e) {
            setError(describeNoticesError(e));
        } finally { setLoading(false); }
    }, [pid]);

    useEffect(() => { load(); }, [load]);

    // #notices 착지 — 소식 현황 탭 환자명 링크. 로딩이 끝난 뒤 한 번만 스크롤
    useEffect(() => {
        if (loading || !anchorRef.current) return;
        if (typeof window !== 'undefined' && window.location && window.location.hash === '#notices') {
            try { anchorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e) { console.error('[보낸 소식] 앵커 이동 실패:', e?.message || e); }
        }
    }, [loading]);

    const more = async () => {
        if (shown < items.length) { setShown(s => s + TIMELINE_PAGE); return; }
        if (!nextCursor || moreBusy) return;
        setMoreBusy(true); setMoreError('');
        try {
            const r = await listPatientNotices(pid, { cursor: nextCursor });
            setItems(prev => [...prev, ...r.items]);
            setNextCursor(r.nextCursor || null);
            setShown(s => s + TIMELINE_PAGE);
        } catch (e) {
            setMoreError(describeNoticesError(e, '더 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.'));
        } finally { setMoreBusy(false); }
    };

    const texts = data?.texts || {};
    const upcoming = data?.upcoming || [];
    const visible = items.slice(0, shown);
    const hasMore = shown < items.length || !!nextCursor;

    const thStyle = { ...U.tableTh, padding: '8px 10px', textAlign: 'left', whiteSpace: 'nowrap' };
    const tdStyle = { ...U.tableTd, padding: '8px 10px', color: '#334155', verticalAlign: 'top' };
    const neutralBadge = { ...U.badge, background: '#f1f5f9', color: '#475569', fontSize: 11 };   // 「먼저 볼 답」 — 텍스트만, 색 강조 없음(ClinicRoadmapInbox 와 동일)
    const smallBtn = { ...U.buttonSecondary, padding: '3px 10px', fontSize: 12 };

    const renderText = (it) => {
        if (it.contentDeleted) return <span title={texts.contentDeletedHint || undefined} style={{ color: '#94a3b8' }}>{texts.contentDeleted || ''}</span>;
        if (!isSendEvent(it.event)) return null;   // 사건 행 — 문구 칸 비움
        return (
            <>
                <div style={{ whiteSpace: 'pre-wrap' }}>{it.text || ''}</div>
                {it.lockText && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{`${texts.lockPrefix || ''}${it.lockText}`}</div>}
            </>
        );
    };
    const renderResult = (it) => {
        if (isSendEvent(it.event)) return it.resultLabel || it.eventLabel || '';
        const parts = [it.eventLabel];
        if (it.event === 'expired' && it.resultLabel) parts.push(it.resultLabel);
        return parts.filter(Boolean).join(' · ');
    };

    return (
        <div id="notices" ref={anchorRef} style={{ ...U.card, marginBottom: 12 }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}><Send size={16} color="#6366f1" /> {texts.cardTitle || '보낸 소식'}</h3>
            {texts.cardSubtitle && <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>{texts.cardSubtitle}</div>}

            {error && (
                <div style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 8 }}>
                    {error} <button type="button" onClick={load} style={{ ...smallBtn, marginLeft: 6 }}>다시 시도</button>
                </div>
            )}
            {loading && <div style={{ fontSize: 13, color: '#94a3b8', padding: '12px 0' }}>불러오는 중...</div>}

            {!loading && data && (
                <>
                    {data.errors.length > 0 && texts.partialError && (
                        <div style={{ fontSize: 12.5, color: '#64748b', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '6px 10px', marginBottom: 8 }}>{texts.partialError}</div>
                    )}

                    {/* 다음에 나갈 것 — 3줄 + 접기 */}
                    <div style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', marginBottom: 4 }}>{texts.upcomingTitle || ''}</div>
                        {upcoming.length === 0 ? (
                            <div style={{ fontSize: 13, color: '#94a3b8' }}>{texts.emptyUpcoming || ''}</div>
                        ) : (
                            <>
                                {(upcomingOpen ? upcoming : upcoming.slice(0, UPCOMING_FOLD)).map((u, i) => (
                                    <div key={`${u.kind}_${u.refId || i}_${u.dueDay}`} style={{ fontSize: 13, color: '#334155', padding: '4px 0', borderTop: i === 0 ? 'none' : '1px solid #f1f5f9' }}>
                                        <span style={{ fontWeight: 600 }}>{fmtDayKey(u.dueDay)}</span>
                                        {' · '}{u.label || (u.kind === 'clinic_notice' ? NOTICE_LABEL_FALLBACK : '')}{u.stageLabel ? ` · ${u.stageLabel}` : ''}
                                        {u.text && <div style={{ color: '#475569', whiteSpace: 'pre-wrap', marginTop: 2 }}>{u.text}</div>}
                                        {u.note && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{u.note}</div>}
                                    </div>
                                ))}
                                {upcoming.length > UPCOMING_FOLD && (
                                    <button type="button" onClick={() => setUpcomingOpen(o => !o)} style={{ ...smallBtn, marginTop: 4 }}>{upcomingOpen ? '접기' : `더 보기 (${upcoming.length - UPCOMING_FOLD}건)`}</button>
                                )}
                            </>
                        )}
                    </div>

                    {/* 타임라인 표 */}
                    {items.length === 0 ? (
                        <div style={{ fontSize: 13, color: '#94a3b8', padding: '8px 0' }}>{texts.emptyTimeline || ''}</div>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr>
                                        <th style={thStyle}>날짜·시각</th>
                                        <th style={thStyle}>종류</th>
                                        <th style={thStyle}>{texts.textColumn || ''}</th>
                                        <th style={thStyle}>결과</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visible.map(it => (
                                        <React.Fragment key={it.id}>
                                            <tr>
                                                <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{fmtAt(it.at)}</td>
                                                <td style={tdStyle}>
                                                    <div style={{ fontWeight: 600, color: '#0f172a' }}>{it.label || (it.kind === 'clinic_notice' ? NOTICE_LABEL_FALLBACK : '')}</div>
                                                    {it.stageLabel && <div style={{ fontSize: 12, color: '#64748b' }}>{it.stageLabel}</div>}
                                                    {it.firstLook && texts.firstLookBadge && <div style={{ marginTop: 2 }}><span style={neutralBadge}>{texts.firstLookBadge}</span></div>}
                                                </td>
                                                <td style={tdStyle}>{renderText(it)}</td>
                                                <td style={tdStyle}>
                                                    {renderResult(it)}
                                                    {Number.isFinite(it.deleteAt) && (it.event === 'answered' || it.event === 'acknowledged') && (
                                                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{`${fmtAt(it.deleteAt, 'short')} 삭제 예정`}</div>
                                                    )}
                                                </td>
                                            </tr>
                                            {it.kind === 'withdrawn' && texts.withdrawnFootnote && (
                                                <tr><td colSpan={4} style={{ ...tdStyle, fontSize: 11, color: '#94a3b8', paddingTop: 0 }}>{texts.withdrawnFootnote}</td></tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {moreError && <div style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 8, padding: '6px 10px', fontSize: 13, marginTop: 8 }}>{moreError}</div>}
                    {hasMore && (
                        <div style={{ marginTop: 8 }}>
                            <button type="button" disabled={moreBusy} onClick={more} style={smallBtn}>{moreBusy ? '불러오는 중...' : '더 보기'}</button>
                            <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 8 }}>{`${visible.length}/${Number.isFinite(data.total) ? data.total : items.length}건`}</span>
                        </div>
                    )}
                    {/* ☆ 서버 texts 에 truncated 전용 문구 없음 — 웹 최소 문구 */}
                    {data.truncated && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>{'오래된 발송 기록 일부는 보이지 않을 수 있습니다.'}</div>}

                    {texts.cardNotice && (
                        <div style={{ fontSize: 12, color: '#64748b', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px', marginTop: 12, lineHeight: 1.5 }}>{texts.cardNotice}</div>
                    )}
                </>
            )}
        </div>
    );
}

export default ClinicPatientNotices;
